import {
    type Absence,
    AbsenceStatus,
    type Employee,
    type Holiday,
    type TimeEntry,
    dateAdd,
    dateSub,
    employeeNameOrderForSort,
    formatDateShort,
    formatNumber,
    formatWeekDayShort,
    getEmployeeName,
    type getVacationEntitlement,
    timeEntryTypeColor,
} from "@pentacode/core";
import type { DateString } from "@pentacode/openapi";
import "@pentacode/ui/components";
import { LitElement, html } from "lit";
import { customElement, property } from "lit/decorators.js";
import { cache } from "lit/directives/cache.js";
import { app, router } from "../../init";

export interface Month {
    name: string;
    from: DateString;
    to: DateString;
    year: number;
    days: {
        date: DateString;
        holiday: Holiday | null;
        dayOfWeek: number;
    }[];
    isVisible: boolean;
}

@customElement("ptc-employee-absences-row")
export class EmployeeAbsencesRow extends LitElement {
    @property({ attribute: false })
    employee: Employee;

    @property({ attribute: false })
    entries: TimeEntry[];

    @property({ attribute: false })
    absences: Absence[];

    @property({ attribute: false })
    vacationEntitlement: ReturnType<typeof getVacationEntitlement> | null = null;

    @property({ type: Boolean })
    vacationBalancesLoading = false;

    @property({ attribute: false })
    months: Month[];

    @property({ attribute: false })
    from: DateString;

    @property({ attribute: false })
    to: DateString;

    @property({ type: Boolean })
    isVisible = false;

    shouldUpdate(changes: Map<string, any>) {
        return changes.has("isVisible") || this.isVisible;
    }

    createRenderRoot() {
        return this;
    }

    render() {
        return html`${cache(this.isVisible ? this._render() : "")}`;
    }

    private async _dragstart(dragEvent: DragEvent, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("drag-start", { detail: { dragEvent, employee, date } }));
    }

    private _dragover(e: DragEvent) {
        e.preventDefault();
        e.dataTransfer!.dropEffect = "copy";
    }

    private _dragenter(dragEvent: DragEvent, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("drag-enter", { detail: { dragEvent, employee, date } }));
    }

    private async _dragend() {
        this.dispatchEvent(new CustomEvent("drag-end"));
    }

    private _drop() {}

    private async _dayClicked(dragEvent: Event, employee: Employee, date: string) {
        this.dispatchEvent(new CustomEvent("day-clicked", { detail: { dragEvent, employee, date } }));
    }

    private async _absenceClicked(absence: Absence) {
        this.dispatchEvent(
            new CustomEvent("absence-clicked", {
                detail: { absence },
            })
        );
    }

    private renderVacationProgress() {
        if (this.vacationBalancesLoading) {
            return html`
                <ptc-ui-progress
                    style="--progress-color-subceeded: black; --progress-color-below: black;"
                    .max=${100}
                    .value=${0}
                    .format=${() => "?"}
                    class="right-margined smaller vacation loading-pulse"
                ></ptc-ui-progress>
            `;
        }

        if (this.vacationEntitlement) {
            return html`
                <ptc-ui-progress
                    style="--progress-color-subceeded: black; --progress-color-below: black;"
                    .max=${this.vacationEntitlement.available}
                    .value=${this.vacationEntitlement.taken}
                    .format=${(val: number) => formatNumber(val, 1)}
                    class="right-margined smaller vacation"
                ></ptc-ui-progress>
            `;
        }

        return "";
    }

    private _render() {
        const employee = this.employee;
        const absences = this.absences.filter((absence) => absence.employeeId === employee.id);
        const entries = this.entries.filter((entry) => entry.employeeId === employee.id);

        return html`
            <div
                class="row-header horizontal spacing center-aligning layout click"
                @click=${() => router.go(`employees/${employee.id}/absences`)}
            >
                <ptc-ui-avatar
                    .employee=${employee}
                    .positionColors=${employee.positions.map((p) => app.company.getPositionColor(p))}
                ></ptc-ui-avatar>
                <div class="stretch ellipsis right-padded" style="font-size: 0.9em;">
                    ${getEmployeeName(employee, employeeNameOrderForSort(app.employeeFilters.sortProperty))}
                </div>
                ${this.renderVacationProgress()}
            </div>
            ${this.months.map(
                ({ days, isVisible }) =>
                    html`<div class="month horizontal layout" style="width: calc(${days.length} * var(--cell-size));">
                        ${cache(
                            isVisible
                                ? html`
                                      ${days.map(({ date, dayOfWeek, holiday }) => {
                                          const entry = entries.find((e) => e.date === date);
                                          const contract = employee.getContractForDate(date);
                                          const blocked =
                                              (!contract || contract.blocked) && (contract?.comment || "inaktiv");
                                          return html`
                                              <div
                                                  class="centering layout click day ${holiday
                                                      ? "violet"
                                                      : dayOfWeek === 0
                                                        ? "orange"
                                                        : ""}"
                                                  title=${holiday
                                                      ? holiday.name
                                                      : `${formatWeekDayShort(date)}, ${formatDateShort(date)}`}
                                                  @click=${(e: Event) => this._dayClicked(e, employee, date)}
                                                  draggable="true"
                                                  @dragstart=${(e: DragEvent) => this._dragstart(e, employee, date)}
                                                  @dragenter=${(e: DragEvent) =>
                                                      this._dragenter(e, employee, dateAdd(date, { days: 1 }))}
                                                  @dragover=${this._dragover}
                                                  @dragend=${this._dragend}
                                                  @drop=${this._drop}
                                              >
                                                  ${entry
                                                      ? html`
                                                            <ptc-ui-icon
                                                                name="${app.localized.timeEntryTypeIcon(entry.type)}"
                                                                class="faded"
                                                                style="color: ${timeEntryTypeColor(entry.type)}"
                                                                title="${app.localized.timeEntryTypeLabel(entry)}"
                                                            ></ptc-ui-icon>
                                                        `
                                                      : blocked
                                                        ? html`
                                                              <ptc-ui-icon
                                                                  name="icon.status.blocked"
                                                                  class="faded"
                                                                  title="${blocked}"
                                                              ></ptc-ui-icon>
                                                          `
                                                        : ""}
                                              </div>
                                          `;
                                      })}
                                  `
                                : ""
                        )}
                    </div>`
            )}
            ${absences.map((absence) => {
                const length = dateSub(absence.start, absence.end);
                const dayIndex = dateSub(this.from, absence.start) + 1;
                return html`
                    <div
                        class="absence ${absence.status}"
                        style="--color-highlight: ${timeEntryTypeColor(absence.type)}; left: calc(${dayIndex +
                        7} * var(--cell-size)); width: calc(${length} * var(--cell-size) - 1px);"
                    >
                        <div
                            class="absence-inner spacing center-aligning horizontal layout click ${absence.status ===
                            AbsenceStatus.Inferred
                                ? "inferred"
                                : ""}"
                            @click=${() => this._absenceClicked(absence)}
                            title="${app.localized.timeEntryTypeLabel({
                                type: absence.type,
                            })}${absence.status === "requested" ? " (Antrag)" : ""}"
                        >
                            <ptc-ui-icon
                                name="${app.localized.timeEntryTypeIcon(absence.type)}"
                                class="smaller"
                                ?hidden=${absence.status === "requested"}
                            ></ptc-ui-icon>
                            <div class="stretch ellipsis">
                                ${absence.notes ||
                                `${formatDateShort(absence.start)} - ${formatDateShort(
                                    dateAdd(absence.end, { days: -1 })
                                )}`}
                            </div>
                            ${absence.status === "requested"
                                ? html`
                                      <ptc-ui-icon name="icon.planning.vacation-request" class="smaller"></ptc-ui-icon>
                                  `
                                : ""}
                        </div>
                    </div>
                `;
            })}
        `;
    }
}
